import React, { useState } from 'react'
import { SlidersHorizontal, ChevronDown } from 'lucide-react'
import type { Filter, Sort } from '../../lib/list'

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
        {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  )
}

export function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`bg-white rounded-xl border border-gray-200 shadow-sm ${className}`}>{children}</div>
}

export function StatCard({ label, value, icon, accent = 'green' }: { label: string; value: string | number; icon?: React.ReactNode; accent?: string }) {
  const accents: Record<string, string> = {
    green: 'bg-green-100 text-green-700',
    blue: 'bg-blue-100 text-blue-700',
    amber: 'bg-amber-100 text-amber-700',
    purple: 'bg-purple-100 text-purple-700',
    red: 'bg-red-100 text-red-700',
  }
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        </div>
        {icon && <div className={`p-3 rounded-lg ${accents[accent] ?? accents.green}`}>{icon}</div>}
      </div>
    </Card>
  )
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-gray-500">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mb-3" />
      {label && <span className="text-sm">{label}</span>}
    </div>
  )
}

export function EmptyState({ title, message }: { title: string; message?: string }) {
  return (
    <div className="text-center py-16">
      <p className="text-lg font-medium text-gray-700">{title}</p>
      {message && <p className="text-sm text-gray-500 mt-1">{message}</p>}
    </div>
  )
}

export function Button({
  children, onClick, variant = 'primary', type = 'button', disabled = false, className = '', title,
}: {
  children: React.ReactNode
  onClick?: () => void
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost'
  type?: 'button' | 'submit'
  disabled?: boolean
  className?: string
  title?: string
}) {
  const variants: Record<string, string> = {
    primary: 'bg-green-700 hover:bg-green-800 text-white',
    secondary: 'bg-white hover:bg-gray-50 text-gray-800 border border-gray-300',
    danger: 'bg-red-600 hover:bg-red-700 text-white',
    ghost: 'text-gray-600 hover:bg-gray-100',
  }
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${variants[variant]} ${className}`}
    >
      {children}
    </button>
  )
}

export function SearchInput({ value, onChange, placeholder = 'Search…' }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <input
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full max-w-sm px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
    />
  )
}

interface Column<T> {
  key: string
  header: string
  render?: (row: T) => React.ReactNode
  className?: string
  // Client-side sort value (used for in-memory tables like detail-page tabs).
  sortValue?: (row: T) => string | number | null | undefined
  // Server-side sort column. When the table is given `sort`/`onSort`, clicking a
  // header with `sortKey` re-queries the whole dataset ordered by this column.
  sortKey?: string
}

export function DataTable<T extends { id: string }>({
  columns, rows, onRowClick, emptyMessage = 'No records found', rowClassName, defaultSort, sort: serverSort, onSort,
}: {
  columns: Column<T>[]
  rows: T[]
  onRowClick?: (row: T) => void
  emptyMessage?: string
  rowClassName?: (row: T) => string
  defaultSort?: { key: string; dir: 'asc' | 'desc' }
  // Server-side sort: current sort + handler. When provided, columns with a
  // `sortKey` sort against the full dataset instead of the current page.
  sort?: Sort | null
  onSort?: (column: string) => void
}) {
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(defaultSort ?? null)

  const sorted = React.useMemo(() => {
    if (onSort || !sort) return rows // server-sorted (or unsorted) → keep order as given
    const col = columns.find(c => c.key === sort.key)
    if (!col?.sortValue) return rows
    const dir = sort.dir === 'asc' ? 1 : -1
    return [...rows].sort((a, b) => {
      const va = col.sortValue!(a), vb = col.sortValue!(b)
      if (va == null && vb == null) return 0
      if (va == null) return 1
      if (vb == null) return -1
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir
      return String(va).localeCompare(String(vb), undefined, { numeric: true }) * dir
    })
  }, [rows, sort, columns, onSort])

  const toggleSort = (key: string) =>
    setSort(prev => prev?.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' })

  const isSortable = (c: Column<T>) => (onSort && c.sortKey) || (!onSort && c.sortValue)
  const headerClick = (c: Column<T>) => {
    if (onSort && c.sortKey) onSort(c.sortKey)
    else if (!onSort && c.sortValue) toggleSort(c.key)
  }
  const activeArrow = (c: Column<T>) => {
    if (onSort) return serverSort && c.sortKey === serverSort.column ? (serverSort.dir === 'asc' ? '▲' : '▼') : null
    return sort?.key === c.key ? (sort.dir === 'asc' ? '▲' : '▼') : null
  }

  if (!rows.length) return <EmptyState title={emptyMessage} />
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            {columns.map(c => (
              <th key={c.key}
                onClick={() => isSortable(c) && headerClick(c)}
                className={`text-left font-semibold text-gray-600 px-4 py-3 ${isSortable(c) ? 'cursor-pointer select-none hover:text-gray-900' : ''} ${c.className ?? ''}`}>
                <span className="inline-flex items-center gap-1">
                  {c.header}
                  {activeArrow(c) && <span className="text-green-600">{activeArrow(c)}</span>}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map(row => (
            <tr
              key={row.id}
              onClick={() => onRowClick?.(row)}
              className={`border-b border-gray-100 ${onRowClick ? 'cursor-pointer hover:bg-green-50' : ''} ${rowClassName?.(row) ?? ''}`}
            >
              {columns.map(c => (
                <td key={c.key} className={`px-4 py-3 text-gray-700 ${c.className ?? ''}`}>
                  {c.render ? c.render(row) : String((row as Record<string, unknown>)[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// -----------------------------------------------------------------------------
// FilterBar — compact, reusable column-filter row. Emits a Filter[] mapped to
// the server query. Supports text (ilike), select (eq), boolean, number (eq),
// and date-range (gte/lte) fields.
// -----------------------------------------------------------------------------
export type FilterFieldType = 'text' | 'select' | 'boolean' | 'number' | 'daterange'
export interface FilterField {
  key: string
  label: string
  column: string
  type: FilterFieldType
  options?: { label: string; value: string }[]
  placeholder?: string
}

export function FilterBar({ fields, onChange, defaultOpen = false }: { fields: FilterField[]; onChange: (filters: Filter[]) => void; defaultOpen?: boolean }) {
  const [vals, setVals] = useState<Record<string, string>>({})
  const [open, setOpen] = useState(defaultOpen)

  const emit = (next: Record<string, string>) => {
    const filters: Filter[] = []
    for (const f of fields) {
      if (f.type === 'daterange') {
        const from = next[`${f.key}_from`]
        const to = next[`${f.key}_to`]
        if (from) filters.push({ column: f.column, op: 'gte', value: from })
        if (to) filters.push({ column: f.column, op: 'lte', value: `${to}T23:59:59.999` })
        continue
      }
      const v = next[f.key]
      if (v === undefined || v === '') continue
      if (f.type === 'text') filters.push({ column: f.column, op: 'ilike', value: v })
      else if (f.type === 'number') filters.push({ column: f.column, op: 'eq', value: Number(v) })
      else if (f.type === 'boolean') filters.push({ column: f.column, op: 'eq', value: v === 'true' })
      else filters.push({ column: f.column, op: 'eq', value: v })
    }
    onChange(filters)
  }

  const set = (key: string, value: string) => {
    const next = { ...vals, [key]: value }
    setVals(next)
    emit(next)
  }
  const clearAll = () => { setVals({}); onChange([]) }

  // Count active filters (a date range counts once if either end is set).
  const activeCount = fields.reduce((n, f) => {
    if (f.type === 'daterange') return n + ((vals[`${f.key}_from`] || vals[`${f.key}_to`]) ? 1 : 0)
    return n + ((vals[f.key] ?? '') !== '' ? 1 : 0)
  }, 0)

  const inputCls = 'px-2.5 py-1.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500'

  if (fields.length === 0) return null

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
            activeCount > 0 ? 'border-green-600 text-green-700 bg-green-50' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
          }`}
        >
          <SlidersHorizontal size={14} /> Filters
          {activeCount > 0 && (
            <span className="ml-0.5 px-1.5 rounded-full bg-green-600 text-white text-xs">{activeCount}</span>
          )}
          <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {activeCount > 0 && (
          <Button variant="ghost" onClick={clearAll}>Clear</Button>
        )}
      </div>
      {open && (
        <div className="flex flex-wrap items-end gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200">
          {fields.map(f => (
            <div key={f.key} className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500">{f.label}</label>
              {f.type === 'text' && (
                <input className={inputCls} value={vals[f.key] ?? ''} placeholder={f.placeholder} onChange={e => set(f.key, e.target.value)} />
              )}
              {f.type === 'number' && (
                <input type="number" className={`${inputCls} w-28`} value={vals[f.key] ?? ''} placeholder={f.placeholder} onChange={e => set(f.key, e.target.value)} />
              )}
              {f.type === 'select' && (
                <select className={inputCls} value={vals[f.key] ?? ''} onChange={e => set(f.key, e.target.value)}>
                  <option value="">All</option>
                  {f.options?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              )}
              {f.type === 'boolean' && (
                <select className={inputCls} value={vals[f.key] ?? ''} onChange={e => set(f.key, e.target.value)}>
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              )}
              {f.type === 'daterange' && (
                <div className="flex items-center gap-1">
                  <input type="date" className={inputCls} value={vals[`${f.key}_from`] ?? ''} onChange={e => set(`${f.key}_from`, e.target.value)} />
                  <span className="text-gray-400 text-xs">to</span>
                  <input type="date" className={inputCls} value={vals[`${f.key}_to`] ?? ''} onChange={e => set(`${f.key}_to`, e.target.value)} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function Tabs({ tabs }: { tabs: { id: string; label: string; badge?: number; content: React.ReactNode }[] }) {
  const [active, setActive] = useState(tabs[0]?.id)
  const current = tabs.find(t => t.id === active) ?? tabs[0]
  return (
    <div>
      <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActive(t.id)}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
              active === t.id ? 'border-green-700 text-green-800' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
            {t.label}
            {t.badge != null && <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs ${active === t.id ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{t.badge}</span>}
          </button>
        ))}
      </div>
      <div className="pt-4">{current?.content}</div>
    </div>
  )
}

export function Pagination({ page, pageSize, total, onPage }: { page: number; pageSize: number; total: number; onPage: (p: number) => void }) {
  const pages = Math.ceil(total / pageSize)
  if (pages <= 1) return null
  return (
    <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
      <span>
        Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} of {total.toLocaleString()}
      </span>
      <div className="flex gap-2">
        <Button variant="secondary" disabled={page === 0} onClick={() => onPage(page - 1)}>Previous</Button>
        <Button variant="secondary" disabled={page >= pages - 1} onClick={() => onPage(page + 1)}>Next</Button>
      </div>
    </div>
  )
}
